import { useEffect, useState } from "react";
import { DesignerMkt } from "../../../models/DesignerMkt";
import { ApiResponse } from "../../../models/ApiResponse";
import api from "../../../services/ApiService";
import { SettingGrid } from "../../../components/ManageSettingComponents/SettingGrid";
import { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import moment from "moment";
import { Button, Switch } from "@mui/material";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { SessionDetails } from "../../../models/SessionDetails";
import MarketingDesignerModal from "./MarketingDesignerModal";
import DeleteValidationModal from "../../../components/ManageSettingComponents/DeleteValidationModal";
import { DATE_TIME_FORMAT } from "../../../utils/constants";
import { GRID_ACTION_DELETE_ICON, GRID_ACTION_EDIT_ICON } from "../../../components/GridUtilityComponents";
import { Permissions } from "../../../models/Enum";
import SessionService from "../../../services/SessionService";

export default function MarketingDesignerSetting() {
  const [marketingDesignerList, setMarketingDesignerList] = useState<
    DesignerMkt[]
  >([]);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);

  const MarketingDesignerGridCollumns: GridColDef[] = [
    { field: "id", headerName: "Id", width: 100 },
    { field: "pimKey", headerName: "PIM Key", width: 100},
    { field: "jdeCode", headerName: "JDE Code", width: 100},
    { field: "designer", headerName: "Designer Name", width: 300 },
    { field: "leagoCode", headerName: "LEAGO Code", width: 160 },
    { field: "prefix", headerName: "Prefix", width: 100 },
    {
      field: "isActive",
      headerName: "Is Active",
      width: 200,
      sortable: false,
      renderCell: (params: any) => (
        <>
          <Switch
            checked={params.value}
            onChange={async (e) => {
              await updateMarketingActive(params.row, e.target.checked);
            }}
            color={"primary"}
            inputProps={{ "aria-label": "controlled" }}
            disabled = {!manageAppSetting}
          />
        </>
      ),
    },
    // {
    //   field: "updatedDate",
    //   headerName: "UpdatedDate",
    //   width: 200,
    //   valueFormatter: (params) => moment(params?.value).format(DATE_TIME_FORMAT),
    // },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      getActions: (params: any) => [
        GRID_ACTION_EDIT_ICON(params, handleEditClickOpen),
        GRID_ACTION_DELETE_ICON(params, handleDeleteModalOpen)
      ],
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const getDesignerMktResponse = await api.get<ApiResponse<DesignerMkt[]>>(
      "/Picklist/designer-mkts"
    );
    setMarketingDesignerList(getDesignerMktResponse?.data || []);
  };

  const handleEditClickOpen = (gridParams: any) => {
    formik.resetForm();
    setShowAddUpdateModal(true);
    setDisableSubmit(false);
    formik.setValues({ ...gridParams.row });
  };

  const handleAddClickOpen = () => {
    formik.resetForm();
    setShowAddUpdateModal(true);
    setDisableSubmit(false);
  };

  const handleDeleteModalOpen = (gridParams: any) => {
    setDeleteModalOpen(true);
    formik.setValues({ ...gridParams.row });
  };

  const handleCloseModal = () => {
    setShowAddUpdateModal(false);
    setDeleteModalOpen(false);
    formik.resetForm();
  };

  const updateMarketingActive = (row: DesignerMkt, isActive: boolean) => {
    row.isActive = isActive;
    try {
      api
        .put<ApiResponse<DesignerMkt>>("/Picklist/designer-mkt", row)
        .then(async (response: any) => {
          if (response.isSuccess) {
            toast.success(response.message);
            await fetchData();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => {});
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const deleteSubmit = () => {
    try {
      api
        .delete<ApiResponse<SessionDetails>>(
          `/Picklist/designer-mkt/${formik.values.id}`
        )
        .then(async (response: any) => {
          if (response.isSuccess) {
            setDeleteModalOpen(false);
            formik.resetForm();
            toast.success(response.message);
            await fetchData();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => {});
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: 0,
      designer: "",
      leagoCode: "",
      prefix: "",
      isActive: true,
      sapCode: null,
      pimKey: "",
      jdeCode: null,
    },
    validationSchema: Yup.object({
      designer: Yup.string()
        .trim()
        .min(1, "Designer Name must be at least 1 characters")
        .max(40, "Designer Name must be at most 40 characters")
        .required("Designer Name is required"),
      leagoCode: Yup.string()
        .trim()
        .required("LEAGO Code is required")
        .test("check-leagoCode-duplicate", "LEAGO Code already exists", (value) => {
          if (!value?.trim()) {
            return true;
          }
          return !marketingDesignerList.some(
            (item) =>
              item.leagoCode?.trim().toLowerCase() === value.trim().toLowerCase() &&
              item.id !== formik.values.id
          );
        }),
      prefix: Yup.string().required("Prefix is required"),
      pimKey: Yup.string()
        .nullable()
        .max(20, "PIM Key must be at most 20 characters")
        .test("check-pimKey-duplicate", "PIM Key already exists", 
          (value) => {
            if(!value?.trim()) {
                return true;
            }
            return !!value?.trim() && !marketingDesignerList.some((item) => item.pimKey?.trim().toLowerCase() === 
            value?.trim().toLowerCase() && item.id !== formik.values.id);
        }),
      jdeCode: Yup.string()
        .nullable()
        .max(20, "JDE Code must be at most 20 characters")
        .test("check-jdeCode-duplicate", "JDE Code already exists",
          (value) => {
            if(!value?.trim()) {
                return true;
            }
            return !marketingDesignerList.some((item) => item.jdeCode?.trim().toLowerCase() ===
            value?.trim().toLowerCase() && item.id !== formik.values.id);
        })
    }),
    onSubmit: (values) => {
      setDisableSubmit(true);
      if (values.id > 0) {
        api
          .put<ApiResponse<number>>("/Picklist/designer-mkt", {
            id: values.id,
            designer: values.designer?.toString()?.trim() || null,
            leagoCode: values.leagoCode?.toString()?.trim(),
            prefix: values.prefix?.toString()?.trim() || null,
            sapCode: values.sapCode?.toString()?.trim() || null,
            isActive: JSON.parse(`${values.isActive}`),
            pimKey: values.pimKey?.trim() || null,
            jdeCode: values.jdeCode?.toString()?.trim() || null
          })
          .then(async (response: any) => {
            if (response.isSuccess) {
              setShowAddUpdateModal(false);
              formik.resetForm();
              toast.success(response.message);
              await fetchData();
            } else {
              throw new Error(response.message);
            }
          })
          .catch(() => {});
      } else {
        api
          .post<ApiResponse<number>>("/Picklist/designer-mkt", {
            designer: values.designer?.toString()?.trim() || null,
            leagoCode: values.leagoCode?.toString()?.trim(),
            prefix: values.prefix?.toString()?.trim() || null,
            sapCode: values.sapCode?.toString()?.trim() || null,
            isActive: JSON.parse(`${values.isActive}`),
            pimKey: values.pimKey?.trim() || null,
            jdeCode: values.jdeCode?.toString()?.trim() || null
          })
          .then(async (response: any) => {
            if (response.isSuccess) {
              setShowAddUpdateModal(false);
              formik.resetForm();
              toast.success(response.message);
              await fetchData();
            } else {
              throw new Error(response.message);
            }
          })
          .catch(() => {});
      }
    },
  });

  return (
    <>
      <div>
        {manageAppSetting && <Button variant="contained" onClick={handleAddClickOpen}>
          Add Marketing Designer
        </Button>}
        <MarketingDesignerModal
          isOpen={showAddUpdateModal}
          onClose={handleCloseModal}
          formik={formik}
          isButtonDisabled={disableSubmit}
        />
        <DeleteValidationModal
          addModalOpen={deleteModalOpen}
          handleCloseModal={handleCloseModal}
          deleteSubmit={deleteSubmit}
          fieldIdentifier={"Marketing Designer"}
        />
      </div>
      <SettingGrid
        data={marketingDesignerList}
        column={MarketingDesignerGridCollumns}
        sortByField="id"
        sortByOrder="desc"
        columnVisibility={{
          actions: manageAppSetting,
        }}
      />
    </>
  );
}
